// Short messages at the bottom of the screen ("Draft saved"), announced to
// screen readers. Errors stay a little longer.
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface Toast {
  id: number;
  message: string;
  error?: boolean;
  action?: { label: string; run: () => void };
}

type Show = (message: string, options?: { error?: boolean; action?: Toast['action'] }) => void;

const ToastContext = createContext<Show>(() => {});

export const useToast = () => useContext(ToastContext);

let next = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback<Show>((message, options) => {
    const toast = { id: next++, message, ...options };
    setToasts((all) => [...all.slice(-2), toast]);
    setTimeout(() => setToasts((all) => all.filter((x) => x.id !== toast.id)), options?.error ? 7000 : 4000);
  }, []);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast${toast.error ? ' toast--error' : ''}`}>
            <span>{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  toast.action!.run();
                  setToasts((all) => all.filter((x) => x.id !== toast.id));
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
